/* Order Documentation Certificate ($7.99) storefront add-on.
 *
 * Mirrors the architecture of priority-processing-upsell.js:
 * single-file IIFE, cart.js as the source of truth, in-place optimistic UI
 * (selector + displayed subtotal paint synchronously in the click frame;
 * server truth stays authoritative — every completion path re-renders from
 * /cart.js, and every failure path rolls back to truth plus a quiet inline
 * error). Like PP, add/remove NEVER reloads the page: totals, the
 * free-shipping bar, and sibling surfaces refresh in place via refreshTotals
 * + pub/sub. A window.fetch guard blocks ineligible direct cart writes and
 * cleans up orphaned lines after third-party writes.
 *
 * Presentation rules:
 * - The offer card copy is static (the checked circle alone signals state).
 * - The sample-modal CTA toggles between "Add Certificate + $7.99" (active)
 *   and "Certificate Added" (disabled confirmation) from /cart.js truth —
 *   synced on every modal open and after every add/remove (paintModalCta).
 *
 * Certificate rules (kept in sync with snippets/certificate-addon-offer.liquid
 * and the cert-checkout-validation function):
 * - Eligible: PAB bundle lines (properties) OR lines whose product carries
 *   the `certificate-eligible` tag (fetched per product handle, cached).
 * - One certificate maximum per order (variant 52752838263060).
 * - Orphaned certificates (no eligible merchandise left) are removed and
 *   the buyer is told why.
 * - The $7.99 stays in every money total but is excluded from
 *   free-shipping threshold math (see window.PMCertCartCents below).
 */
(function () {
  'use strict';

  var CERT_VARIANT_ID = '52752838263060';
  var CERT_SKU = 'ORDER-CERTIFICATE';
  var CERT_PRICE_CENTS_FALLBACK = 399;
  /* Live certificate price: server-rendered on every offer card via
   * data-cert-price-cents (sourced from the Order Certificate variant).
   * Falls back to 399 when the attribute is missing or unparseable. */
  function certPriceCents() {
    try {
      var box = document.querySelector('[data-cert-offer][data-cert-price-cents]');
      var cents = parseInt(box && box.getAttribute('data-cert-price-cents'), 10);
      if (isFinite(cents) && cents > 0) return cents;
    } catch (_e) {}
    return CERT_PRICE_CENTS_FALLBACK;
  }
  var ELIGIBLE_TAG = 'certificate-eligible';
  /* Checkout links here to show this very same theme modal. The preview tab
   * must never change the storefront cart behind an active checkout. */
  var checkoutSamplePreview =
    window.location.pathname === '/cart' &&
    new URLSearchParams(window.location.search).get('certificate_sample') === 'checkout';

  var PAB_VARIANT_IDS = {
    '52502897590548': true,
    '52674499019028': true
  };

  var SYNC_TIMEOUT_MS = 5000;
  var SYNC_POLL_MS = 50;
  var ADD_VERIFY_RETRY_MS = 400;
  var TAG_CACHE_TTL_MS = 5 * 60 * 1000;

  var INELIGIBLE_MESSAGE =
    'The Order Certificate is only available with eligible prop money items.';
  var ORPHAN_NOTICE = 'Certificate removed \u2014 a prop money item is required.';
  var ADD_FAILED_NOTICE = 'We couldn\u2019t add the certificate. Please try again.';
  var REMOVE_FAILED_NOTICE = 'We couldn\u2019t remove the certificate. Please try again.';

  function str(value) {
    return value == null ? '' : String(value);
  }

  function numericId(value) {
    var raw = str(value);
    if (!raw) return '';
    var match = raw.match(/(\d+)$/);
    return match ? match[1] : raw;
  }

  function propsOf(item) {
    if (!item) return {};
    if (item.properties && typeof item.properties === 'object') return item.properties;
    return {};
  }

  function normalizeLine(item) {
    if (!item) return { variant_id: '', sku: '', handle: '', properties: {} };
    var merch = item.merchandise || {};
    var product = merch.product || item.product || {};
    return {
      variant_id: numericId(item.variant_id || item.variantId || merch.id),
      sku: str(item.sku || merch.sku),
      handle: str(item.handle || product.handle || (item.product && item.product.handle)),
      properties: propsOf(item)
    };
  }

  function isCertLine(item) {
    var line = normalizeLine(item);
    if (line.variant_id === CERT_VARIANT_ID) return true;
    if (line.sku.toUpperCase() === CERT_SKU) return true;
    return false;
  }

  /* Gift cards document no prop money purchase: never certificate-eligible.
   * Mirrors the Liquid skip in snippets/certificate-addon-offer.liquid. */
  function isGiftCardLine(item) {
    if (!item) return false;
    if (item.gift_card === true) return true;
    var line = normalizeLine(item);
    return str(line.handle).toLowerCase() === 'gift-card';
  }

  function isPabLine(item) {
    var line = normalizeLine(item);
    if (isCertLine(item)) return false;
    var props = line.properties || {};
    if (props._custom_build) return true;
    if (props.Tier && props.Stacks && props._build_id) return true;
    if (PAB_VARIANT_IDS[line.variant_id] && (props._custom_build || props.Tier)) return true;
    if (props._parent || props._feature || props._unique_build) return true;
    if (props._pab_adjustment_line || props._pab_pricing_model) return true;
    if (str(props._build_id).trim()) return true;
    return false;
  }

  function cartItems(cart) {
    if (!cart) return [];
    if (Array.isArray(cart.items)) return cart.items;
    return [];
  }

  function certLines(cart) {
    return cartItems(cart).filter(isCertLine);
  }

  function cartHasCert(cart) {
    return certLines(cart).some(function (line) {
      return Number(line.quantity || 0) > 0;
    });
  }

  function certCents(cart) {
    return certLines(cart).reduce(function (sum, line) {
      return sum + Number(line.final_line_price || 0);
    }, 0);
  }

  /* Per-product tag cache: /products/<handle>.js is public and includes tags. */
  var tagCache = {};
  var tagInFlight = {};

  function tagsForHandle(handle) {
    var key = str(handle).toLowerCase();
    if (!key) return Promise.resolve([]);
    var cached = tagCache[key];
    if (cached && Date.now() - cached.at < TAG_CACHE_TTL_MS) {
      return Promise.resolve(cached.tags);
    }
    if (tagInFlight[key]) return tagInFlight[key];
    var promise = fetch('/products/' + encodeURIComponent(key) + '.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    })
      .then(function (res) {
        /* A failed tag lookup must never poison the cache: a transient
         * failure cached as [] would make every later eligibility check
         * (including orphan removal) treat a qualifying cart as
         * ineligible for the full TTL. */
        if (!res.ok) return { tags: [], cache: false };
        return res
          .json()
          .then(function (product) {
            var tags = product && Array.isArray(product.tags) ? product.tags : [];
            return { tags: tags, cache: true };
          })
          .catch(function () {
            return { tags: [], cache: false };
          });
      })
      .catch(function () {
        return { tags: [], cache: false };
      })
      .then(function (result) {
        if (result.cache) tagCache[key] = { tags: result.tags, at: Date.now() };
        delete tagInFlight[key];
        return result.tags;
      });
    tagInFlight[key] = promise;
    return promise;
  }

  function lineIsTagEligible(item) {
    var line = normalizeLine(item);
    if (!line.handle) return Promise.resolve(false);
    return tagsForHandle(line.handle).then(function (tags) {
      return tags.indexOf(ELIGIBLE_TAG) !== -1;
    });
  }

  async function cartEligibleForCert(cart) {
    var items = cartItems(cart).filter(function (item) {
      return !isCertLine(item) && !isGiftCardLine(item);
    });
    for (var i = 0; i < items.length; i += 1) {
      if (isPabLine(items[i])) return true;
    }
    var candidates = items.filter(function (item) {
      return !isPabLine(item);
    });
    for (var j = 0; j < candidates.length; j += 1) {
      // eslint-disable-next-line no-await-in-loop
      if (await lineIsTagEligible(candidates[j])) return true;
    }
    return false;
  }

  function needsOrphanCertRemoval(cart, eligible) {
    return cartHasCert(cart) && !eligible;
  }

  async function fetchCart() {
    var res = await fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' });
    if (!res.ok) throw new Error('cart.js failed (' + res.status + ')');
    return res.json();
  }

  function currentFetch() {
    return window.fetch.bind(window);
  }

  async function setCertQuantity(quantity) {
    var res = await currentFetch()('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: { [CERT_VARIANT_ID]: quantity } })
    });
    if (!res.ok) {
      var body = await res.text().catch(function () {
        return '';
      });
      throw new Error('update.js failed (' + res.status + ') ' + body);
    }
    return res.json();
  }

  async function changeLineByKey(key, quantity) {
    var res = await currentFetch()('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    });
    if (!res.ok) throw new Error('change.js failed (' + res.status + ')');
    return res.json();
  }

  /* ---------- UI sync ---------- */

  function offerContainers() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-cert-offer]'));
  }

  /* The PDP slot only renders for `certificate-eligible` products (Liquid
   * gate in product-form.liquid), so a pdp-surface box's presence means the
   * product being viewed qualifies — even when the cart itself is empty. */
  function pdpSurfaceEligible() {
    return offerContainers().some(function (box) {
      return box.getAttribute('data-cert-surface') === 'pdp';
    });
  }

  function setCertToggles(hasCert) {
    document.querySelectorAll('[data-cert-toggle]').forEach(function (input) {
      input.checked = !!hasCert;
    });
  }

  function setOfferVisibility(eligible, hasCert) {
    setCertToggles(hasCert);
    offerContainers().forEach(function (box) {
      var boxEligible = eligible || box.getAttribute('data-cert-surface') === 'pdp';
      box.hidden = !boxEligible;
      box.setAttribute('data-cert-state', hasCert ? 'added' : 'offer');
      var notice = box.querySelector('[data-cert-notice]');
      if (notice) {
        notice.hidden = true;
        notice.textContent = '';
      }
    });
    paintModalCta(hasCert);
  }

  /* PP-parity optimistic paint: flip selector + displayed subtotal
   * synchronously in the click frame, before any network round-trip.
   * Card copy is static, so there are no panels to swap — only the checked
   * circle (via setCertToggles), the modal CTA, and the subtotal paint.
   * Server truth stays authoritative — every completion path re-renders from
   * /cart.js, and every failure path rolls back to truth plus the existing
   * inline error. Shipping-threshold (FS/FedEx) math is deliberately NOT
   * touched here: it only ever recomputes from real cart data. */
  function paintDisplayedSubtotal(cents) {
    var text = formatMoney(cents);
    document
      .querySelectorAll('[data-atc-banner-cart-subtotal],[data-cart-total]')
      .forEach(function (el) {
        el.textContent = text;
      });
  }

  function paintOptimistic(hasCert) {
    setCertToggles(hasCert);
    offerContainers().forEach(function (box) {
      box.setAttribute('data-cert-state', hasCert ? 'added' : 'offer');
    });
    paintModalCta(hasCert);
    cartOptions.desired('cert', hasCert);
  }

  function showCertNotice(message, tone) {
    var shown = false;
    offerContainers().forEach(function (box) {
      var notice = box.querySelector('[data-cert-notice]');
      if (!notice) return;
      box.hidden = false;
      notice.textContent = message;
      if (tone) notice.setAttribute('data-tone', tone);
      else notice.removeAttribute('data-tone');
      notice.hidden = false;
      shown = true;
    });
    return shown;
  }

  /* Quiet inline error that leaves panels/state untouched, so the caller can
   * keep the verified offer state visible (never a false-positive "added"). */
  function showCertInlineError(message) {
    offerContainers().forEach(function (box) {
      var notice = box.querySelector('[data-cert-notice]');
      if (!notice) return;
      box.hidden = false;
      notice.textContent = message;
      notice.removeAttribute('data-tone');
      notice.hidden = false;
    });
  }

  /* PP-parity busy lock: purely internal (certOpsInFlight). Nothing is ever
   * disabled or dimmed mid-flight — the card keeps its exact resting
   * appearance while the request runs, and rapid taps serialize on the
   * in-flight counter inside add/removeCertificate. Releasing the lock
   * repaints the modal CTA so a finished op can't leave a stale button:
   * when the certificate is in the cart it stays a disabled "Certificate
   * Added" confirmation. */
  function setButtonsBusy(busy) {
    if (!busy) paintModalCta(lastSync.hasCert);
  }

  function formatMoney(cents) {
    try {
      if (window.PMCartMoneyFormat && typeof window.PMCartMoneyFormat.formatCents === 'function') {
        return window.PMCartMoneyFormat.formatCents(cents);
      }
    } catch (_e) {}
    return '$' + (Number(cents || 0) / 100).toFixed(2);
  }

  function refreshTotals(cart) {
    if (!cart) return;
    var cents = certCents(cart);
    window.PMCertCartCents = cents;
    var cartSection = document.querySelector('[data-cart-section]');
    if (cartSection) cartSection.setAttribute('data-certificate-cart-cents', String(cents));
    lastPaintedTotal = cart.total_price;
    document.querySelectorAll('[data-atc-banner-cart-subtotal]').forEach(function (el) {
      el.textContent = formatMoney(cart.total_price);
    });
    document.querySelectorAll('[data-atc-banner-cart-button]').forEach(function (btn) {
      if (btn.textContent.indexOf('View Cart') === -1) btn.textContent = 'View Cart & Checkout';
    });
    try {
      if (typeof window.updateCartTotals === 'function' && document.querySelector('[data-cart-section]')) {
        window.updateCartTotals(cart);
      } else if (typeof window.PMCartRefreshSubtotal === 'function' && document.querySelector('[data-cart-section]')) {
        window.PMCartRefreshSubtotal();
      } else {
        document.querySelectorAll('[data-cart-total]').forEach(function (el) {
          el.textContent = formatMoney(cart.total_price);
        });
      }
    } catch (_e) {
      document.querySelectorAll('[data-cart-total]').forEach(function (el) {
        el.textContent = formatMoney(cart.total_price);
      });
    }
    try {
      if (typeof window.PMCartUpdateFreeShipping === 'function') {
        window.PMCartUpdateFreeShipping(cart.total_price);
      }
    } catch (_e) {}
    cartOptions.accept(cart);
  }

  function publishCartUpdate(cart) {
    try {
      if (typeof publish === 'function' && window.PUB_SUB_EVENTS && PUB_SUB_EVENTS.cartUpdate) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'certificate-addon',
          cartData: cart,
          variantId: CERT_VARIANT_ID
        });
      }
    } catch (_e) {}
  }

  /* ---------- mutations ---------- */

  var cartOptions = window.PMCartOptionCoordinator;
  var certOpsInFlight = 0;
  var certDesired = null;
  var lastSync = { eligible: false, hasCert: false };
  /* Last server-confirmed cart total (cents). Optimistic paints derive the
     displayed subtotal from this — never from guessed cart math. */
  var lastPaintedTotal = null;

  function certSyncIsPending() {
    return certOpsInFlight > 0;
  }

  async function waitForCertSyncBeforeNavigation() {
    var startedAt = Date.now();
    while (certSyncIsPending()) {
      if (Date.now() - startedAt >= SYNC_TIMEOUT_MS) return;
      await new Promise(function (resolve) {
        setTimeout(resolve, SYNC_POLL_MS);
      });
    }
  }

  window.PMCartWaitForCert = async function () {
    await waitForCertSyncBeforeNavigation();
    if (certSyncIsPending()) throw new Error('Certificate is still updating. Please try again.');
  };

  async function removeOrphanedCert(cart, options) {
    var opts = options || {};
    var lines = certLines(cart).filter(function (line) {
      return Number(line.quantity || 0) > 0;
    });
    if (!lines.length) return { cart: cart, removed: false };
    var eligible = await cartEligibleForCert(cart);
    if (!eligible && pdpSurfaceEligible()) return { cart: cart, removed: false };
    if (!needsOrphanCertRemoval(cart, eligible)) return { cart: cart, removed: false };
    /* The verdict above may rest on a stale snapshot (a concurrent sync or
     * a just-superseded cart). Re-verify against fresh server truth before
     * the destructive write so a slow sync can never delete a certificate
     * the buyer's current cart still qualifies for. */
    var fresh = await fetchCart();
    if (!cartHasCert(fresh)) return { cart: fresh, removed: false };
    var freshEligible = await cartEligibleForCert(fresh);
    if (!needsOrphanCertRemoval(fresh, freshEligible)) return { cart: fresh, removed: false };
    var freshCart = await setCertQuantity(0);
    return { cart: freshCart, removed: true };
  }

  async function dedupeAndNormalize(cart) {
    var lines = certLines(cart).filter(function (line) {
      return Number(line.quantity || 0) > 0;
    });
    if (!lines.length) return { cart: cart, changed: false };
    var totalQty = lines.reduce(function (sum, line) {
      return sum + Number(line.quantity || 0);
    }, 0);
    if (lines.length === 1 && totalQty === 1) return { cart: cart, changed: false };
    if (lines.length === 1) {
      var fixed = await setCertQuantity(1);
      return { cart: fixed, changed: true };
    }
    var keepKey = lines[0].key;
    var freshCart = cart;
    for (var i = 1; i < lines.length; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      freshCart = await changeLineByKey(lines[i].key, 0);
    }
    var kept = certLines(freshCart).filter(function (line) {
      return line.key === keepKey;
    })[0];
    if (kept && Number(kept.quantity || 0) !== 1) {
      freshCart = await changeLineByKey(keepKey, 1);
    }
    return { cart: freshCart, changed: true };
  }

  /* Recency guard: several syncs can overlap (boot + cartcount:update +
   * section refresh + pub/sub), and each one awaits slow tag lookups. Only
   * the newest sync may paint UI or publish — a stale sync resolving late
   * must never clobber a fresher subtotal, toggle state, or free-shipping
   * bar (the "PP added but subtotal didn't move" symptom). The returned
   * cart truth is still handed back to every caller. */
  var certSyncEpoch = 0;

  async function syncCertStateFromCart(providedCart, options) {
    var opts = options || {};
    var myEpoch = ++certSyncEpoch;
    var cart = providedCart && Array.isArray(providedCart.items) ? providedCart : await fetchCart();
    if (cartOptions.busy()) return cart;
    if (!opts.skipOrphanRemoval) {
      var orphanResult = await removeOrphanedCert(cart, { silent: !!opts.silent });
      cart = orphanResult.cart;
      if (orphanResult.removed) {
        await syncCertStateFromCart(cart, { skipOrphanRemoval: true, silent: true });
        /* A real removal already happened server-side: always publish (drives
         * sibling features and the header cart count). The recursive sync
         * above already repainted toggles + totals in place from fresh truth —
         * no reload on any surface. Only the cosmetic notice is recency-gated. */
        publishCartUpdate(cart);
        if (!opts.silent && myEpoch === certSyncEpoch) {
          showCertNotice(ORPHAN_NOTICE);
        }
        return cart;
      }
    }
    var normalized = await dedupeAndNormalize(cart);
    cart = normalized.cart;
    var eligible = await cartEligibleForCert(cart);
    var hasCert = cartHasCert(cart);
    if (myEpoch !== certSyncEpoch) return cart;
    lastSync = { eligible: eligible, hasCert: hasCert };
    setOfferVisibility(eligible, hasCert);
    refreshTotals(cart);
    cartOptions.accept(cart);
    return cart;
  }

  /* Optimistic add (PP parity): the selector + subtotal paint synchronously,
   * then server truth decides. A silent reject (HTTP 200 with no line), a
   * race with a third-party writer, or stale cart state rolls the buyer back
   * to the offer state with a quiet inline error — the confirmed "added"
   * state (modal CTA, reconciled totals) only ever rests on /cart.js proof.
   * Rapid taps serialize on certOpsInFlight + disabled buttons; one-per-order
   * is enforced by dedupeAndNormalize (and the checkout validation fn). */
  async function applyCertificateChoice(wantCert) {
    return cartOptions.run(async function () {
      var cart = await fetchCart();
      if (wantCert) {
        var eligible = (await cartEligibleForCert(cart)) || pdpSurfaceEligible();
        if (!eligible) {
          var ineligible = new Error(INELIGIBLE_MESSAGE);
          ineligible.userMessage = INELIGIBLE_MESSAGE;
          throw ineligible;
        }
        if (!cartHasCert(cart)) {
          var res = await currentFetch()('/cart/add.js', {
            method: 'POST', credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: Number(CERT_VARIANT_ID), quantity: 1 }] })
          });
          if (!res.ok) throw new Error('add.js failed (' + res.status + ')');
          cart = await fetchCart();
          if (!cartHasCert(cart)) {
            await new Promise(function (resolve) { setTimeout(resolve, ADD_VERIFY_RETRY_MS); });
            cart = await fetchCart();
          }
          if (!cartHasCert(cart)) throw new Error('add.js returned without certificate line');
        }
        cart = (await dedupeAndNormalize(cart)).cart;
      } else if (cartHasCert(cart)) {
        await setCertQuantity(0);
        cart = await fetchCart();
        if (cartHasCert(cart)) {
          await new Promise(function (resolve) { setTimeout(resolve, ADD_VERIFY_RETRY_MS); });
          await setCertQuantity(0);
          cart = await fetchCart();
        }
        if (cartHasCert(cart)) throw new Error('certificate line remained after remove');
      }
      return cart;
    });
  }

  async function drainCertificateChoices() {
    if (certOpsInFlight) return;
    certOpsInFlight = 1;
    try {
      while (certDesired !== null) {
        var target = certDesired;
        certDesired = null;
        var cart;
        var failure = null;
        try {
          cart = await applyCertificateChoice(target);
        } catch (error) {
          failure = error;
          try { cart = await fetchCart(); } catch (_ignored) {}
        }
        if (cart) cartOptions.accept(cart);
        // A newer click wins. Never repaint the old result or show an error
        // for an intent that the buyer has already replaced.
        if (certDesired !== null) continue;
        if (cart) {
          var eligible = await cartEligibleForCert(cart);
          if (certDesired !== null) continue;
          var hasCert = cartHasCert(cart);
          lastSync = { eligible: eligible, hasCert: hasCert };
          setOfferVisibility(eligible, hasCert);
          refreshTotals(cart);
          cartOptions.accept(cart);
          publishCartUpdate(cart);
        }
        if (!cart) setCertToggles(lastSync.hasCert);
        cartOptions.clear('cert');
        if (failure || !cart || cartHasCert(cart) !== target) {
          showCertInlineError(failure && failure.userMessage ||
            (target ? ADD_FAILED_NOTICE : REMOVE_FAILED_NOTICE));
        }
      }
    } finally {
      certOpsInFlight = 0;
      setButtonsBusy(false);
      if (certDesired !== null) drainCertificateChoices();
    }
  }

  function requestCertificate(wantCert) {
    ++certSyncEpoch;
    certDesired = !!wantCert;
    paintOptimistic(certDesired);
    drainCertificateChoices();
  }

  function addCertificate() { requestCertificate(true); }
  function removeCertificate() { requestCertificate(false); }

  /* ---------- quickshop in-modal sample view (mobile) ----------
   * The collection quickshop modal is lifted to z-index 99999, so the
   * body-level sample modal (z-index 10000) opens behind it — the same
   * class of issue as the pickup-details modal. On phones (below the
   * theme's L breakpoint) the sample dialog is stacked and fits, so the
   * sample is shown inside the open Options modal with a back action
   * instead. Desktop and every other surface keep the body-level path.
   * The options DOM is hidden in place (never removed), so the selected
   * variant, quantity, and all other form state survive the round trip. */
  var QS_SAMPLE_MAX_WIDTH = '(max-width: 1023px)';
  var qsSampleActive = false;
  var qsSampleHome = null;
  var qsSampleInvoker = null;
  var qsSampleObserver = null;
  /* Held node reference: Empire DESTROYS the quickshop subtree on close, so
   * by the time any close observer runs, document queries can no longer find
   * the relocated sample node. The live reference survives detachment and is
   * the only safe handle for restoring it. */
  var qsSampleModal = null;

  function isMobileQuickshopSample(invoker) {
    if (!invoker || typeof invoker.closest !== 'function') return false;
    if (!window.matchMedia || !window.matchMedia(QS_SAMPLE_MAX_WIDTH).matches) return false;
    return !!invoker.closest('.modal--quickshop-slim, .modal--quickshop-full');
  }

  function qsSampleContext(invoker) {
    var host =
      invoker && typeof invoker.closest === 'function'
        ? invoker.closest('.modal--quickshop-slim, .modal--quickshop-full')
        : null;
    if (!host) return null;
    var content = host.querySelector('[data-modal-content]');
    return content ? { modal: host, content: content } : null;
  }

  function qsSampleBackBtn() {
    var btn = document.querySelector('[data-qs-cert-back]');
    if (btn) return btn;
    btn = document.createElement('button');
    btn.setAttribute('type', 'button');
    btn.setAttribute('data-qs-cert-back', '');
    /* Same control language as the pickup-details in-modal back action. */
    btn.setAttribute('class', 'quickshop-pickup-view__back');
    btn.setAttribute('aria-label', 'Back to product options');
    btn.innerHTML =
      '<svg class="quickshop-pickup-view__back-chevron" width="10" height="16" viewBox="0 0 10 16" fill="none" aria-hidden="true" focusable="false"><path d="M8.5 1.5 2 8l6.5 6.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg><span>Options</span>';
    btn.addEventListener('click', function (event) {
      /* Contain the view-switch click so modal-level/document-level close
       * delegates never treat it as a dismiss tap. */
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      qsSampleBack();
    });
    return btn;
  }

  function enterQsSampleView(modal, invoker) {
    var ctx = qsSampleContext(invoker);
    if (!ctx) return false;
    if (!qsSampleHome) qsSampleHome = { parent: modal.parentNode, next: modal.nextSibling };
    var back = qsSampleBackBtn();
    var children = ctx.content.children;
    for (var i = 0; i < children.length; i++) {
      if (children[i] !== back && children[i] !== modal) children[i].style.display = 'none';
    }
    ctx.content.insertBefore(back, ctx.content.firstChild);
    back.style.removeProperty('display');
    if (modal.parentNode !== ctx.content) ctx.content.appendChild(modal);
    modal.classList.add('pm-cert-modal--in-quickshop');
    qsSampleModal = modal;
    qsSampleActive = true;
    watchQsSampleClose();
    ctx.modal.scrollTop = 0;
    return true;
  }

  function qsSampleBack() {
    unwatchQsSampleClose();
    qsSampleActive = false;
    /* Use the held reference, never a document query: the host subtree may
     * already be detached (Empire destroys quickshop DOM on close), in which
     * case querySelector can no longer find the node but the reference can
     * still re-home it. */
    var modal = qsSampleModal || certModal();
    qsSampleModal = null;
    if (modal) {
      modal.classList.remove('pm-cert-modal--in-quickshop');
      try {
        if (qsSampleHome && qsSampleHome.parent && document.contains(qsSampleHome.parent)) {
          var homeNext =
            qsSampleHome.next && document.contains(qsSampleHome.next) ? qsSampleHome.next : null;
          qsSampleHome.parent.insertBefore(modal, homeNext);
        } else {
          document.body.appendChild(modal);
        }
      } catch (_restoreError) {
        try {
          document.body.appendChild(modal);
        } catch (_fallbackError) {}
      }
      try {
        modal.hidden = true;
      } catch (_hideError) {}
    }
    document.documentElement.classList.remove('pm-cert-modal-open');
    var back = document.querySelector('[data-qs-cert-back]');
    if (back) back.remove();
    var contents = document.querySelectorAll(
      '.modal--quickshop-slim [data-modal-content], .modal--quickshop-full [data-modal-content]'
    );
    for (var i = 0; i < contents.length; i++) {
      var kids = contents[i].children;
      for (var j = 0; j < kids.length; j++) {
        if (!kids[j].hasAttribute('data-qs-cert-back')) kids[j].style.removeProperty('display');
      }
    }
    if (
      qsSampleInvoker &&
      typeof qsSampleInvoker.focus === 'function' &&
      document.contains(qsSampleInvoker)
    ) {
      try {
        qsSampleInvoker.focus();
      } catch (_e) {}
    }
    qsSampleInvoker = null;
  }

  /* If the quickshop modal itself closes (X, ATC success, Escape) while the
   * sample view is showing, Empire DESTROYS the quickshop subtree — the
   * relocated sample node goes with it unless re-homed from the held
   * reference. Watch body class AND childList: either the modal-visible flag
   * dropping or the node leaving the document triggers the restore. */
  function watchQsSampleClose() {
    unwatchQsSampleClose();
    qsSampleObserver = new MutationObserver(function () {
      if (!qsSampleActive) return;
      if (!document.body.className.match(/modal-visible/)) {
        qsSampleBack();
        return;
      }
      if (qsSampleModal && !document.contains(qsSampleModal)) qsSampleBack();
    });
    qsSampleObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
      childList: true
    });
  }

  function unwatchQsSampleClose() {
    if (qsSampleObserver) {
      qsSampleObserver.disconnect();
      qsSampleObserver = null;
    }
  }

  /* ---------- sample modal ---------- */

  var lastFocused = null;

  function certModal() {
    return document.querySelector('[data-cert-modal]');
  }

  /* Sample-modal CTA states. "offer" = active black primary
   * ("Add Certificate + $7.99"); "added" = disabled, non-actionable
   * confirmation ("Certificate Added", no price, no add action). Staffed only
   * from /cart.js truth: every open re-syncs fresh, and setOfferVisibility
   * repaints after every add/remove on any surface. */
  function modalCtaOfferLabel() {
    return 'Add Certificate + ' + formatMoney(certPriceCents());
  }
  var MODAL_CTA_ADDED_LABEL = 'Certificate Added';

  function paintModalCta(hasCert) {
    var modal = certModal();
    if (!modal) return;
    var cta = modal.querySelector('[data-cert-add]');
    if (!cta) return;
    if (checkoutSamplePreview) {
      cta.textContent = 'Return to checkout to add';
      cta.removeAttribute('disabled');
      cta.removeAttribute('aria-disabled');
      cta.setAttribute('data-cert-modal-state', 'offer');
      return;
    }
    if (hasCert) {
      cta.textContent = MODAL_CTA_ADDED_LABEL;
      cta.setAttribute('disabled', '');
      cta.setAttribute('aria-disabled', 'true');
      cta.setAttribute('data-cert-modal-state', 'added');
    } else {
      cta.textContent = modalCtaOfferLabel();
      cta.setAttribute('data-cert-modal-state', 'offer');
      /* Mid-flight the CTA stays visually actionable (PP parity — no
       * dimming); taps during the flight are absorbed by the
       * certOpsInFlight guard inside addCertificate. */
      cta.removeAttribute('disabled');
      cta.removeAttribute('aria-disabled');
    }
  }

  /* ---------- in-place sample zoom (no second modal) ---------- */

  var ZOOM_IN_LABEL = 'Click to zoom';
  var ZOOM_OUT_LABEL = 'Fit to page';
  /* A tap that pans the enlarged document must not toggle the zoom: finger
   * travel beyond this (px) means "scroll", not "tap". */
  var ZOOM_PAN_SLOP_PX = 8;

  function certDialog() {
    var modal = certModal();
    return modal ? modal.querySelector('.pm-cert-modal__dialog') : null;
  }

  function zoomPill() {
    var modal = certModal();
    return modal ? modal.querySelector('.pm-cert-modal__zoom-pill') : null;
  }

  function setCertZoom(on) {
    var dialog = certDialog();
    if (dialog) dialog.classList.toggle('is-zoomed', !!on);
    var pill = zoomPill();
    if (pill) {
      pill.textContent = on ? ZOOM_OUT_LABEL : ZOOM_IN_LABEL;
      pill.setAttribute('aria-pressed', on ? 'true' : 'false');
    }
    /* Zoom shows the full-resolution source (1700w) so small text stays
       crisp; normal view keeps the lighter 1100w render. */
    var modal = certModal();
    var img = modal ? modal.querySelector('.pm-cert-modal__art img') : null;
    if (img) {
      var swap = on ? img.getAttribute('data-zoom-src') : img.getAttribute('data-normal-src');
      if (swap && img.getAttribute('src') !== swap) img.setAttribute('src', swap);
    }
  }

  function isCertZoomed() {
    var dialog = certDialog();
    return !!(dialog && dialog.classList.contains('is-zoomed'));
  }

  var zoomPanStart = null;
  var certPinchActive = false;
  var certPinchClickUntil = 0;

  function blockCertPinch(event) {
    if (!modalIsOpen()) return;
    if (event.type === 'wheel') {
      if (event.ctrlKey) {
        if (event.cancelable) event.preventDefault();
        event.stopPropagation();
      }
      return;
    }
    var touches = event.touches ? event.touches.length : 0;
    var isGesture = event.type.indexOf('gesture') === 0;
    if (event.type === 'touchstart' && touches === 1) {
      certPinchActive = false;
      certPinchClickUntil = 0;
    }
    if (touches > 1 || isGesture) {
      certPinchActive = true;
      certPinchClickUntil = Date.now() + 800;
      zoomPanStart = null;
    }
    if (certPinchActive) {
      if (event.cancelable) event.preventDefault();
      event.stopPropagation();
      certPinchClickUntil = Date.now() + 800;
      if (event.type === 'gestureend' || ((event.type === 'touchend' || event.type === 'touchcancel') && touches === 0)) {
        certPinchActive = false;
      }
    }
  }

  function onZoomPointerDown(event) {
    var target = event.target;
    var toggle =
      target && typeof target.closest === 'function'
        ? target.closest('[data-cert-zoom-toggle]')
        : null;
    zoomPanStart = toggle
      ? { x: event.clientX || 0, y: event.clientY || 0 }
      : null;
  }

  function onZoomToggle(event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return false;
    var toggle = target.closest('[data-cert-zoom-toggle]');
    if (!toggle) return false;
    if (certPinchActive || Date.now() < certPinchClickUntil) {
      event.preventDefault();
      event.stopPropagation();
      zoomPanStart = null;
      return true;
    }
    if (
      toggle.tagName === 'IMG' &&
      zoomPanStart &&
      typeof event.clientX === 'number'
    ) {
      var dx = event.clientX - zoomPanStart.x;
      var dy = event.clientY - zoomPanStart.y;
      if (Math.sqrt(dx * dx + dy * dy) > ZOOM_PAN_SLOP_PX) {
        /* A pan, not a tap: keep the zoom state, but still swallow the
         * release-click so it can never bubble out to the drawer handler. */
        event.stopPropagation();
        zoomPanStart = null;
        return true;
      }
    }
    event.preventDefault();
    /* Capture + stop: the toggle must never reach Empire's document-bubble
     * drawer handler (zooming must not close the modal OR the ATC drawer
     * beneath it). */
    event.stopPropagation();
    setCertZoom(!isCertZoomed());
    zoomPanStart = null;
    return true;
  }

  function openCertModal(invoker) {
    var modal = certModal();
    if (!modal) return;
    /* Mobile quickshop reuses the open Options modal for the sample view.
     * Every other surface keeps the existing body-level path unchanged. */
    var qsMode = isMobileQuickshopSample(invoker) && enterQsSampleView(modal, invoker);
    if (qsMode) {
      qsSampleInvoker =
        invoker && typeof invoker.focus === 'function' ? invoker : document.activeElement;
    }
    certPinchActive = false;
    certPinchClickUntil = 0;
    /* Every open starts at normal size. */
    setCertZoom(false);
    /* Warm the full-resolution zoom source so zooming in never shows an
       upscaled-then-swapped flicker. */
    var zoomImg = modal.querySelector('.pm-cert-modal__art img');
    var zoomSrc = zoomImg ? zoomImg.getAttribute('data-zoom-src') : null;
    if (zoomSrc) { var warmer = new Image(); warmer.src = zoomSrc; }
    /* Prefer the explicit invoker (mouse clicks often leave
       document.activeElement on <body>, Safari included) so focus can be
       returned to the exact View Sample control on close. */
    lastFocused =
      invoker && typeof invoker.focus === 'function' ? invoker : document.activeElement;
    modal.hidden = false;
    document.documentElement.classList.add('pm-cert-modal-open');
    bindModalIsolation();
    /* Paint instantly from last verified truth, then reconcile against fresh
       /cart.js so the CTA is never stale (added elsewhere, another tab). */
    paintModalCta(lastSync.hasCert);
    fetchCart()
      .then(function (cart) {
        if (modalIsOpen()) paintModalCta(cartHasCert(cart));
      })
      .catch(function () {});
    var close = modal.querySelector('[data-cert-modal-close]');
    if (qsMode) {
      var back = document.querySelector('[data-qs-cert-back]');
      if (back && typeof back.focus === 'function') back.focus();
    } else if (close && typeof close.focus === 'function') close.focus();
  }

  function closeCertModal() {
    /* In the quickshop sample view, "close" means back to options — the
     * host modal owns the only close X. */
    if (qsSampleActive) {
      qsSampleBack();
      return;
    }
    var modal = certModal();
    if (!modal || modal.hidden) return;
    setCertZoom(false);
    modal.hidden = true;
    document.documentElement.classList.remove('pm-cert-modal-open');
    if (lastFocused && typeof lastFocused.focus === 'function') {
      try {
        lastFocused.focus();
      } catch (_e) {}
    }
  }

  /* The sample modal renders at the end of <body>, outside Empire's ATC
   * flyout. Empire closes that flyout on any document-bubble click outside
   * [data-atc-banner] (+ touchstart) and on window-bubble Escape. Our modal
   * handlers below run on document/window CAPTURE, so they fire first: every
   * interaction that starts inside the modal is stopped before it can reach
   * Empire — closing the sample modal never closes the ATC drawer beneath
   * it, and Escape/Tab resolve inside the topmost modal first. */
  var modalIsolationBound = false;

  function modalIsOpen() {
    var modal = certModal();
    return !!(modal && !modal.hidden);
  }

  function eventInModal(event) {
    var target = event && event.target;
    if (!target || typeof target.closest !== 'function') return false;
    return !!target.closest('[data-cert-modal]');
  }

  function bindModalIsolation() {
    if (modalIsolationBound) return;
    modalIsolationBound = true;
    ['touchstart', 'touchmove', 'touchend', 'touchcancel', 'gesturestart', 'gesturechange', 'gestureend', 'wheel'].forEach(function (type) {
      document.addEventListener(type, blockCertPinch, { capture: true, passive: false });
    });
    /* Touch devices: Empire's document-bubble touchstart would close the
       drawer before the tap's click event ever fires. stopPropagation (not
       preventDefault) keeps the tap's click synthesis intact. */
    document.addEventListener(
      'touchstart',
      function (event) {
        if (modalIsOpen() && eventInModal(event)) event.stopPropagation();
      },
      true
    );
    /* Capture on window fires before Empire's window-bubble drawer Escape
       handler, so Escape closes only the topmost modal. */
    window.addEventListener(
      'keydown',
      function (event) {
        if (event.key === 'Escape' && modalIsOpen()) {
          event.preventDefault();
          event.stopPropagation();
          closeCertModal();
        }
      },
      true
    );
  }

  function trapTabInModal(event) {
    var modal = certModal();
    if (!modal) return;
    var focusables = modal.querySelectorAll(
      'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (!focusables.length) {
      event.preventDefault();
      return;
    }
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* ---------- fetch guard ---------- */

  function bodyTextOf(init) {
    var body = init && init.body;
    if (typeof body === 'string') return body;
    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
      return body.toString();
    }
    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      var parts = [];
      body.forEach(function (value, key) {
        parts.push(encodeURIComponent(key) + '=' + encodeURIComponent(str(value)));
      });
      return parts.join('&');
    }
    return '';
  }

  function requestAddsCert(url, bodyText) {
    if (!/\/cart\/add(\.js)?/i.test(str(url))) return false;
    if (bodyText.indexOf(CERT_VARIANT_ID) !== -1) return true;
    if (bodyText.toUpperCase().indexOf(CERT_SKU) !== -1) return true;
    return false;
  }

  function isCartWrite(url, method) {
    return /\/cart\/(add|update|change)(\.js)?/i.test(str(url)) && str(method).toUpperCase() !== 'GET';
  }

  function jsonResponse(status, payload) {
    return new Response(JSON.stringify(payload), {
      status: status,
      headers: { 'Content-Type': 'application/json' }
    });
  }

  function cartFromResponse(parsed) {
    /* Orphan cleanup must only ever evaluate FULL carts (/cart/update.js,
     * /cart/change.js, /cart.js — all carry item_count/total_price).
     * /cart/add.js answers an `items: [...]` POST with `{ items: [added] }`
     * (added lines only, no cart totals): treating that pseudo-cart as the
     * whole order makes every just-added certificate look orphaned and the
     * guard deletes it again — the "couldn't add the certificate" loop. */
    if (!parsed || !Array.isArray(parsed.items)) return null;
    if (typeof parsed.item_count !== 'number') return null;
    if (typeof parsed.total_price !== 'number') return null;
    return parsed;
  }

  function installCertWriteGuard() {
    if (window.__PM_CERT_CART_GUARD__) return;
    window.__PM_CERT_CART_GUARD__ = true;
    var downstreamFetch = window.fetch.bind(window);

    window.fetch = async function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var method = str((init && init.method) || (input && input.method) || 'GET').toUpperCase();
      var text = bodyTextOf(init);

      if (isCartWrite(url, method) && requestAddsCert(url, text)) {
        var allowed = pdpSurfaceEligible();
        if (!allowed) {
          try {
            var preCart = await fetchCart();
            allowed = await cartEligibleForCert(preCart);
          } catch (_e) {
            allowed = false;
          }
        }
        if (!allowed) {
          return jsonResponse(422, {
            status: 422,
            description: INELIGIBLE_MESSAGE,
            message: INELIGIBLE_MESSAGE
          });
        }
      }

      var res = await downstreamFetch(input, init);
      if (isCartWrite(url, method) && res.ok) {
        try {
          var parsed = await res.clone().json();
          var cart = cartFromResponse(parsed);
          if (cart && certLines(cart).length) {
            var eligible = await cartEligibleForCert(cart);
            if (needsOrphanCertRemoval(cart, eligible)) {
              var cleaned = await removeOrphanedCert(cart, { silent: true });
              publishCartUpdate(cleaned.cart);
              syncCertStateFromCart(cleaned.cart, { skipOrphanRemoval: true, silent: true }).catch(
                function () {}
              );
              return jsonResponse(200, cleaned.cart || cart);
            }
          }
        } catch (_e) {}
      }
      return res;
    };
  }

  /* ---------- events ---------- */

  function onOfferClick(event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;
    if (target.closest('[data-cert-sample]')) {
      event.preventDefault();
      /* Capture fires before Empire's document-bubble drawer handler: opening
         or closing the modal must never toggle the ATC drawer beneath it. */
      event.stopPropagation();
      openCertModal(target.closest('[data-cert-sample]'));
      return;
    }
    if (target.closest('[data-cert-modal-close]')) {
      event.preventDefault();
      event.stopPropagation();
      closeCertModal();
      return;
    }
    /* In-place zoom toggle (image or pill). Handled here on capture so the
     * tap can never bubble out to the theme's drawer-close handler. */
    if (onZoomToggle(event)) return;
    var addBtn = target.closest('[data-cert-add]');
    if (addBtn) {
      event.preventDefault();
      event.stopPropagation();
      if (checkoutSamplePreview) {
        window.close();
        if (!window.closed) addBtn.textContent = 'Switch to your checkout tab to add';
        return;
      }
      addCertificate();
      return;
    }
    if (target.closest('[data-cert-remove]')) {
      event.preventDefault();
      event.stopPropagation();
      removeCertificate();
    }
  }

  function onModalKeydown(event) {
    if (!modalIsOpen()) return;
    if (event.key === 'Escape') {
      /* Document-capture beats Empire's window-bubble drawer Escape. */
      event.preventDefault();
      event.stopPropagation();
      closeCertModal();
      return;
    }
    /* Keep Tab cycling inside the topmost modal instead of falling through
       to Empire's flyout focus trap behind it. Inside the quickshop sample
       view the host modal owns the trap (back action, sample, and its own
       close X must all stay reachable), so stand down here. */
    if (event.key === 'Tab') {
      if (qsSampleActive) return;
      event.stopPropagation();
      trapTabInModal(event);
    }
  }

  /* PP-style toggle card: checking adds, unchecking removes. add/remove are
   * serialized on certOpsInFlight (never via disabled styling — the card
   * keeps its resting appearance mid-flight), so rapid clicks cannot create
   * qty > 1; every completion path re-syncs the toggle from /cart.js truth
   * via setOfferVisibility. */
  function onCertToggleChange(event) {
    var input = event.target;
    if (!input || !input.matches || !input.matches('[data-cert-toggle]')) return;
    if (input.disabled) return;
    if (input.checked) addCertificate();
    else removeCertificate();
  }

  var checkoutNavInFlight = false;

  function onCheckoutNavigation(event) {
    if (checkoutNavInFlight || !certSyncIsPending()) return;
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;
    var trigger = target.closest(
      'button[name="checkout"], #CartDrawer-Checkout, a[href*="/checkout"]'
    );
    if (!trigger || trigger.closest('[data-atc-banner-cart-button]')) return;
    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
    checkoutNavInFlight = true;
    waitForCertSyncBeforeNavigation()
      .catch(function () {})
      .then(function () {
        checkoutNavInFlight = false;
        if (trigger.tagName === 'A' && trigger.href) {
          window.location.assign(trigger.href);
        } else if (trigger.tagName === 'BUTTON' && trigger.type !== 'submit') {
          trigger.click();
        } else if (trigger.form && typeof trigger.form.requestSubmit === 'function') {
          trigger.form.requestSubmit(trigger);
        } else if (trigger.form) {
          trigger.form.submit();
        }
      });
  }

  function onExternalCartUpdate(event) {
    if (cartOptions.busy()) return;
    if (certOpsInFlight > 0) return;
    if (event && event.source === 'certificate-addon') return;
    var snapshot =
      event && event.cartData && Array.isArray(event.cartData.items) ? event.cartData : null;
    syncCertStateFromCart(snapshot, { skipOrphanRemoval: false }).catch(function () {});
  }

  function onCartCountUpdate(event) {
    if (cartOptions.busy()) return;
    if (certOpsInFlight > 0) return;
    var cart = event && event.detail;
    var snapshot = cart && Array.isArray(cart.items) ? cart : null;
    queueMicrotask(function () {
      syncCertStateFromCart(snapshot, { skipOrphanRemoval: false }).catch(function () {});
    });
  }

  function onSectionRefreshed(event) {
    if (cartOptions.busy()) return;
    if (certOpsInFlight > 0) return;
    if (event && event.detail && event.detail.source === 'certificate-addon') return;
    syncCertStateFromCart(null, { skipOrphanRemoval: false }).catch(function () {});
  }

  document.addEventListener('click', onOfferClick, true);
  document.addEventListener('pointerdown', onZoomPointerDown, true);
  document.addEventListener('change', onCertToggleChange, true);
  document.addEventListener('click', onCheckoutNavigation, true);
  document.addEventListener('submit', onCheckoutNavigation, true);
  document.addEventListener('keydown', onModalKeydown, true);
  document.addEventListener('cart:section-refreshed', onSectionRefreshed);
  window.addEventListener('cartcount:update', onCartCountUpdate);
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted || certOpsInFlight > 0) return;
    syncCertStateFromCart(null, { skipOrphanRemoval: false }).catch(function () {});
  });
  if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS && PUB_SUB_EVENTS.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, onExternalCartUpdate);
  }

  /* ATC-drawer-open reconciliation: the drawer is a fresh clone that inherits
   * the template's hidden state, and exactly one post-ATC sync decides
   * visibility. If that sync bailed (coordinator busy / cert op in flight) or
   * resolved against a transiently failing tag lookup, the card would sit
   * hidden until the next cart event. Every drawer open therefore re-runs the
   * existing fresh-truth sync — epoch-guarded inside
   * syncCertStateFromCart, so only the newest sync may paint. This observes
   * only the Empire open transition and never writes cart state. */
  function onAtcDrawerOpened() {
    if (cartOptions.busy()) return;
    if (certOpsInFlight > 0) return;
    syncCertStateFromCart(null).catch(function () {});
  }
  function handleAtcBannerMutation(mutations) {
    for (var i = 0; i < mutations.length; i += 1) {
      var mutation = mutations[i];
      if (mutation.type === 'attributes') {
        var target = mutation.target;
        if (
          target && target.matches && target.matches('[data-atc-banner]') &&
          target.getAttribute('data-animation-state') === 'open'
        ) {
          onAtcDrawerOpened();
          return;
        }
      } else if (mutation.type === 'childList') {
        var added = mutation.addedNodes;
        for (var j = 0; j < added.length; j += 1) {
          var node = added[j];
          if (node && node.nodeType === 1 && node.matches && node.matches('[data-atc-banner]')) {
            /* Backstop for a banner inserted already open (no later
             * attribute write to observe). A freshly cloned closed banner is
             * ignored here — its closed=>open transition fires the branch
             * above — so one open yields exactly one sync. */
            if (node.getAttribute('data-animation-state') !== 'open') continue;
            onAtcDrawerOpened();
            return;
          }
          if (node && node.nodeType === 1 && node.querySelector) {
            var banner = node.querySelector('[data-atc-banner][data-animation-state="open"]');
            if (banner) {
              onAtcDrawerOpened();
              return;
            }
          }
        }
      }
    }
  }
  var atcDrawerOpenObserver = null;
  if (typeof MutationObserver === 'function' && document.documentElement) {
    atcDrawerOpenObserver = new MutationObserver(handleAtcBannerMutation);
    atcDrawerOpenObserver.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-animation-state']
    });
  }

  window.PMCertAddon = window.PMCertAddon || {};
  window.PMCertAddon.sync = function () {
    return syncCertStateFromCart(null, { skipOrphanRemoval: false });
  };

  async function boot() {
    if (checkoutSamplePreview) {
      openCertModal(null);
      return;
    }
    installCertWriteGuard();
    if (typeof window.PMCertCartCents !== 'number') window.PMCertCartCents = 0;
    try {
      await syncCertStateFromCart(null, { skipOrphanRemoval: false });
    } catch (_e) {}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
